import {Component, Inject, LOCALE_ID} from '@angular/core';
import {Role} from '../../../model/auth/Role';
import {MobileAwareComponent} from '../../../common/mobile-aware/mobile-aware.component';
import {BreakpointObserver} from '@angular/cdk/layout';
import {Contest} from '../../../model/contest/Contest';
import {UserExamGrade, UserExamGradeStats, UserExamStats} from '../../../model/stats/Stats';
import {NotificationHandler} from '../../../helper/notification.handler';
import {StatsService} from '../../../service/stats.service';
import {DEFAULT_PAGE, Page} from '../../../model/Page';
import {School} from '../../../model/location/School';
import {LocationService} from '../../../service/location.service';
import {TenantService} from '../../../service/tenant.service';
import {ContestTaskType, localizeContestTaskType} from '../../../model/contest/ContestTask';
import {Locale} from '../../../model/Locale';
import {ActivatedRoute} from '@angular/router';
import {ContestService} from '../../../service/contest.service';
import {forkJoin, Observable} from 'rxjs';

@Component({
  selector: 'app-exam-school-result-stats',
  templateUrl: './exam-school-results-stats.component.html',
  styleUrls: ['./exam-school-results-stats.component.scss']
})
export class ExamSchoolResultsStatsComponent extends MobileAwareComponent {

  private readonly PAGE_SIZE = 200;

  readonly roles = [Role.Host, Role.Coordinator];

  contestId?: string;
  regionId?: string;
  schoolId?: string;

  competitionId?: string;

  contest?: Contest;

  regions: Map<string, string> = new Map<string, string>();
  examStats: UserExamStats[] | null = null;

  schools: School[] = [];
  schoolStats = new Map<string, UserExamStats[]>();
  schoolGradeStats = new Map<string, UserExamGradeStats>();

  inProgress = false;

  page: Page<never> = DEFAULT_PAGE;

  constructor(private statsService: StatsService,
              private contestService: ContestService,
              private locationService: LocationService,
              private tenantService: TenantService,
              private route: ActivatedRoute,
              @Inject(LOCALE_ID) private locale: string,
              breakpointObserver: BreakpointObserver) {
    super(breakpointObserver);
  }

  initContent() {
    this.inProgress = true;

    const tenant = this.tenantService.getTenant();
    if (tenant && tenant.id) this.competitionId = tenant.id;

    this.route.params.subscribe(params => {
      if (params) {
        if (params.contestId) this.contestId = params.contestId as string;
        if (params.regionId && params.regionId != '-1') this.regionId = params.regionId as string;

        this.schoolId = this.route.snapshot.queryParams.school_id as string;

        this.loadContest();
        this.loadUserParticipationStats(true);
      }
    });

    this.loadRegions();
  }

  private loadRegions(): void {
    this.locationService.getRegions(this.locale as Locale).subscribe(regions => {
      regions.forEach(region => this.regions.set(region.id, region.name));
    });
  }

  private loadContest() {
    if (!this.contestId) return;

    this.contestService.getDetails(this.contestId).subscribe(contest => {
      this.contest = contest;
    }, error => {
      NotificationHandler.handleError($localize`:Notifications|Contest details fetch has failed message@@notifications-contest-details-fetch-failed-error:Contest details fetch has failed`, error);
      this.inProgress = false;
    });
  }


  loadSchools(schoolIds: string[]) {
    this.inProgress = true;

    const observables: Observable<School>[] = [];

    schoolIds.forEach(schoolId => observables.push(this.locationService.getSchoolDetails(schoolId)));

    forkJoin(observables).subscribe(schools => {
      schools.sort((s1, s2) => (s1.region_name || '').localeCompare(s2.region_name || ''));
      this.schools = schools;

      this.inProgress = false;
    }, error => {
      NotificationHandler.handleError($localize`:Notifications|School details fetch has failed message@@notifications-school-details-fetch-failed-error:School details fetch has failed`, error);
    });
  }

  loadUserParticipationStats(reset = false) {
    if (!this.competitionId || !this.contestId || !this.regionId) return;

    if (reset) {
      this.page = {
        max_items: this.PAGE_SIZE,
        asc: false
      };
      this.examStats = null;
      this.schoolStats.clear();
      this.schools = [];
    }

    this.inProgress = true;

    const schoolId = this.schoolId;
    const regionId = this.regionId;
    this.statsService.getAllUserParticipationStats(this.competitionId, this.contestId, regionId, schoolId, this.page).subscribe(page => {
      const data = page.content || [];
      if (reset) {
        this.examStats = data;
      } else {
        if (!this.examStats) {
          this.examStats = data;
        } else {
          this.examStats = this.examStats.concat(data);
        }
      }

      this.page.next_token = page.next_token;

      if (this.page.next_token) {
        this.loadUserParticipationStats();
      } else {
        this.page = {
          max_items: this.PAGE_SIZE,
          asc: false
        };

        this.loadUserExamStats();
      }
    }, error => {
      NotificationHandler.handleError($localize`:Notifications|Stats fetch has failed message@@notifications-stats-fetch-failed-error:Statistical data fetch has failed`, error);
    });
  }

  loadUserExamStats() {
    if (!this.competitionId || !this.contestId || !this.regionId) return;

    this.inProgress = true;

    const schoolId = this.schoolId;
    const regionId = this.regionId;
    this.statsService.getAllUserExamStats(this.competitionId, this.contestId, regionId, schoolId, this.page).subscribe(page => {
      const data = page.content || [];

      if (!this.examStats) {
        this.examStats = data;
      } else {
        this.examStats = this.examStats.concat(data);
      }


      this.page.next_token = page.next_token;

      if (this.page.next_token) {
        this.loadUserExamStats();
      } else if (!this.examStats.length) {
        this.inProgress = false;
      } else {
        this.examStats
          .filter(stats => !!stats.schoolId)
          .forEach(stats => {
            const schoolId = stats.schoolId as string;

            if (!this.schoolStats.has(schoolId)) this.schoolStats.set(schoolId, []);
            const statsData = this.schoolStats.get(schoolId) as UserExamStats[];
            statsData.push(stats);

            if (!this.schoolGradeStats.has(schoolId)) this.schoolGradeStats.set(schoolId, {
              excellent_count: 0,
              good_count: 0,
              average_count: 0
            });
            const gradeStats = this.schoolGradeStats.get(schoolId) as UserExamGradeStats;
            if (stats.grade == UserExamGrade.excellent) gradeStats.excellent_count++;
            if (stats.grade == UserExamGrade.good) gradeStats.good_count++;
            if (!stats.grade || stats.grade == UserExamGrade.average) gradeStats.average_count++;
          });

        this.schoolStats.forEach(stats => {
          stats.sort((i1, i2) => {
            const grade1 = `${i1.schoolGrade}-${i1.taskType}`;
            const grade2 = `${i2.schoolGrade}-${i2.taskType}`;

            if (grade1 != grade2) return grade1.localeCompare(grade2);

            if ((i2.points || 0) != (i1.points || 0)) return (i2.points || 0) - (i1.points || 0);

            const name1 = `${i1.firstName} ${i1.lastName}`;
            const name2 = `${i2.firstName} ${i2.lastName}`;

            return name1.localeCompare(name2);
          });
        });

        this.loadSchools(Array.from(this.schoolStats.keys()));
      }
    }, error => {
      NotificationHandler.handleError($localize`:Notifications|Stats fetch has failed message@@notifications-stats-fetch-failed-error:Statistical data fetch has failed`, error);
    });
  }

  localizeTaskType(taskType?: ContestTaskType) {
    return taskType ? localizeContestTaskType(taskType) : '';
  }

}
